/**
 * One codebase, several Community listings.
 *
 * Figma's Community is searched by name. Someone hunting for "InDesign to
 * Figma" and someone hunting for "PDF to Figma" run two different searches,
 * and neither finds a listing named for the other — so an importer that reads
 * both publishes two plugins. What it must not do is become two codebases:
 * that was tried in `illustrator-to-figma` and the copies drifted. Everything
 * that genuinely differs between the listings is a row in the app's
 * `variants.json` — a name, a Figma plugin id, a domain, a drop-zone line, a
 * tip on how to export from the source app — and everything else is shared,
 * down to a single deploy.
 *
 * The first row is the plugin the app has always published: its id, name and
 * domain are what every existing install is bound to, and the UI brands itself
 * as that row until the sandbox handshake says otherwise. Later rows carry a
 * slug for an `id` while they wait to be created in Figma; the build emits
 * their manifests all the same, so the moment the id is known it is a one-field
 * edit, and a Community link is offered only once the id is Figma's numeric one.
 *
 * Two consumers read the table, which is why it is JSON rather than code:
 * `build.mjs` (one sandbox bundle and one manifest per variant) and the app's
 * own `variants.ts`, which hands the parsed table to {@link defineVariants}.
 */

export interface Variant {
  /** Directory under `plugin/`, and the telemetry slug. */
  key: string;
  /**
   * The Figma manifest id. Figma assigns a numeric one at publish time and
   * every install is bound to it; treat a published one as fixed, since changing
   * it orphans the installs onto a plugin that no longer receives updates. An
   * unpublished variant carries a slug (its key) so it can still be imported
   * from its manifest for development — Figma accepts any id there.
   */
  id: string;
  /** Plugin name: the Community listing, the manifest, the plugin window. */
  name: string;
  /**
   * Where this plugin's UI is served from, and the only origin its manifest
   * lets Figma frame. Several variants are usually custom domains on one
   * deployment; they differ here so the network-access notice a designer
   * approves at install names the product they are installing.
   */
  ui: string;
  /** One-line blurb under the title in the UI. */
  subtitle: string;
  /** `accept` attribute for the file picker. */
  accept: string;
  /** Drop-zone headline. */
  dropHint: string;
  /** Suffix on an imported section's name, e.g. "poster · 800 × 600 · EPS → Figma". */
  brand: string;
  /** Human-readable format list, for the manifest's network-access reasoning. */
  formats: string;
  /**
   * How to get a file this variant imports out of the application it is named
   * for — "File → Export → Adobe PDF (Interactive)". Shown in the UI; the whole
   * reason a listing named for a source app is worth having.
   */
  tip?: string;
}

export interface VariantTable {
  /** The Cloudflare project that serves every variant's UI; its preview URLs are allowed in dev. */
  pagesProject?: string;
  /** Dev-server origins the manifests allow. */
  devUi: string[];
  variants: Variant[];
  /**
   * Plugin ids of listings this app no longer publishes, mapped to the variant that absorbed
   * them. `{ "1675139847663932791": "illustrator-to-figma" }`.
   *
   * RETIRING A PUBLISHED LISTING DOES NOT UNINSTALL IT. Figma keeps serving the last bundle
   * uploaded, and everyone who already installed it keeps it — so its sandbox goes on sending
   * that id in the handshake forever. The UI answers an unknown id by ignoring it and staying
   * on "Connecting to Figma…", which means dropping the row outright would hang the panel for
   * every existing user rather than merging them into the surviving listing.
   *
   * So the row leaves the table — nothing is built or published for it — and the id stays
   * recognised, resolving to whichever listing took over. Only published ids need an entry; an
   * unpublished one has no installs to strand.
   */
  retiredIds?: Record<string, string>;
}

export interface Variants {
  VARIANTS: Variant[];
  /** The first row: the plugin the app has always published. */
  DEFAULT_VARIANT: Variant;
  /** The variant this sandbox bundle was built for; the default when no build define says. */
  ACTIVE_VARIANT: Variant;
  /** Resolve the plugin that sent a handshake. `undefined` means "not one of ours". */
  variantById(id: string): Variant | undefined;
  variantByKey(key: string): Variant | undefined;
}

/**
 * The table, with lookups and the active row. `activeKey` is the esbuild
 * define `__VARIANT_KEY__` in a sandbox bundle; the UI and the tests pass
 * nothing and get the default.
 */
export function defineVariants(table: VariantTable, activeKey?: string): Variants {
  const VARIANTS = table.variants;
  const DEFAULT_VARIANT = VARIANTS[0];
  if (!DEFAULT_VARIANT) throw new Error('variants.json lists no variants');
  const variantByKey = (key: string): Variant | undefined => VARIANTS.find((v) => v.key === key);
  const variantById = (id: string): Variant | undefined => {
    if (id === '') return undefined;
    const live = VARIANTS.find((v) => v.id === id);
    if (live) return live;
    // A retired listing's installs still hand over their own id; resolve them to the survivor
    // rather than leaving the panel on "Connecting to Figma…" forever. See `retiredIds`.
    const absorbedBy = table.retiredIds?.[id];
    return absorbedBy === undefined ? undefined : variantByKey(absorbedBy);
  };
  return {
    VARIANTS,
    DEFAULT_VARIANT,
    ACTIVE_VARIANT: (activeKey !== undefined ? variantByKey(activeKey) : undefined) ?? DEFAULT_VARIANT,
    variantById,
    variantByKey,
  };
}

/** The Community page for a published plugin id. */
export function communityUrl(id: string): string {
  return `https://www.figma.com/community/plugin/${id}`;
}

/**
 * The Community page for whichever variant is served from `host`, or
 * `undefined` when the host is none of ours or the variant is unpublished.
 */
export function communityUrlForHost(variants: readonly Variant[], host: string): string | undefined {
  const variant = variants.find((v) => isPublished(v) && new URL(v.ui).host === host);
  return variant && communityUrl(variant.id);
}

/**
 * Whether Figma has assigned this variant its id — a slug means it is not yet in the Community.
 *
 * Takes only the `id` so a caller holding something that is not a full variant can still ask.
 * `apps/redirect` is the one that needs that: html-to-figma predates this package and has a
 * plain manifest, and the question "is this published" is identical for it.
 */
export function isPublished(variant: Pick<Variant, 'id'>): boolean {
  return /^\d+$/.test(variant.id);
}

/**
 * What the table must satisfy before it is safe to build from. Returned rather
 * than thrown so an app's test can list every problem at once.
 */
export function tableProblems(table: VariantTable): string[] {
  const problems: string[] = [];
  if (table.variants.length === 0) problems.push('no variants');

  /*
   * A retired id must still land somewhere, and must not shadow a live listing.
   *
   * The whole point of the map is that installs of a merged listing keep resolving; an entry
   * pointing at a key that no longer exists resolves to `undefined`, which is the hang it was
   * added to prevent — and it would do so silently, because nothing else reads it.
   */
  for (const [id, key] of Object.entries(table.retiredIds ?? {})) {
    if (!table.variants.some((v) => v.key === key)) {
      problems.push(`retired id ${id} points at "${key}", which is not a variant`);
    }
    if (table.variants.some((v) => v.id === id)) {
      problems.push(`retired id ${id} is also a live listing's id`);
    }
  }
  /*
   * `ui` is NOT in this list, and that is the point.
   *
   * It was, while every variant had a host of its own. Variants of one app now SHARE a UI host:
   * one deploy serves them all, and which listing a window belongs to comes from the `pluginId`
   * handshake, not from the hostname. Requiring `ui` to be unique would forbid the arrangement
   * that fixed the variants whose own hosts were never attached to anything.
   *
   * The fields that remain are the ones where sharing really is a bug. Two variants with one
   * `id` means the second overwrites the first for anyone who installs both; two with one `name`
   * means a name resolves to either of them; two with one `key` means the build writes both to
   * the same directory.
   */
  for (const field of ['key', 'name', 'brand'] as const) {
    const seen = table.variants.map((v) => v[field]);
    if (new Set(seen).size !== seen.length) problems.push(`two variants share a ${field}: ${seen.join(', ')}`);
  }
  const ids = table.variants.map((v) => v.id).filter((id) => id !== '');
  if (new Set(ids).size !== ids.length) problems.push(`two variants share an id: ${ids.join(', ')}`);
  for (const v of table.variants) {
    if (!/^[a-z0-9-]+$/.test(v.key)) problems.push(`${v.key}: key must be a lowercase slug`);
    /*
     * THE NAME IS A HOST, NOT A TITLE. Every plugin in the family is called
     * `<something>.tomake.design` — it is what Figma shows in the install prompt, what the
     * listing is titled, and what a person types into a browser to find it. Thirteen variants
     * had drifted to prose ("Office to Design") because a human name reads better in a file,
     * and nothing noticed.
     *
     * `brand` must match, since it is the same name in a different place — the suffix on the
     * artboard placed on the canvas. Two fields naming one product are two chances to disagree.
     */
    if (!/^[a-z0-9-]+\.tomake\.design$/.test(v.name)) {
      problems.push(`${v.key}: name must be <plugin>.tomake.design, not ${JSON.stringify(v.name)}`);
    }
    if (v.brand !== v.name) {
      problems.push(`${v.key}: brand must match name (${v.brand} vs ${v.name})`);
    }
    if (!/^[A-Za-z0-9-]+$/.test(v.id)) problems.push(`${v.key}: id must be Figma's numeric id, or the key as a placeholder`);
    try {
      if (new URL(v.ui).protocol !== 'https:') problems.push(`${v.key}: ui must be https`);
    } catch {
      problems.push(`${v.key}: ui is not a URL`);
    }
  }
  return problems;
}
